import { AnimatePresence, motion } from 'framer-motion'
import { Building2, ClipboardList, FileText, Layers, MessageSquare, Package, Search, Users, FolderKanban } from 'lucide-react'
import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useAppData } from '../context/AppDataContext'
import { useUI } from '../context/UIContext'
import { useSettled } from '../hooks/useSettled'
import { cn } from '../lib/cn'
import { HIT_GROUP, hitPath, type Hit } from '../lib/search'
import { STATUS_LABEL, progressOf, type Project as StudioProject } from '../pages/projects/types'
import { useApi } from '../useApi'

interface Result {
  id: string
  title: string
  subtitle: string
  icon: typeof Search
  to: string
  group: string
}

const HIT_ICON: Record<Hit['kind'], typeof Search> = {
  client: Building2,
  person: Users,
  project: Layers,
  task: ClipboardList,
  piece: FileText,
  bucket: Package,
  item: Package
}

/** A hit from the server in the shape this list already draws. */
function asResult(hit: Hit): Result {
  return {
    id: hit.id,
    title: hit.title,
    subtitle: hit.subtitle,
    icon: HIT_ICON[hit.kind],
    to: hitPath(hit),
    group: HIT_GROUP[hit.kind]
  }
}

export function CommandPalette() {
  const { commandPaletteOpen, setCommandPaletteOpen } = useUI()
  const { tasks, clients, projects, team, content } = useAppData()
  const [query, setQuery] = useState('')
  const navigate = useNavigate()

  // The real projects — the long jobs. `projects` above is this month's cycles
  // under an older name, which is why the two groups below read differently.
  const { result: projectResult } = useApi<StudioProject[]>('/projects')
  const studioProjects = projectResult.data ?? []

  // The server looks everywhere — including at things this screen never loads,
  // like what is inside a bucket. Asked a beat after the typing stops, so a
  // long word is one request rather than nine.
  const asked = useSettled(query.trim(), 180)
  const { result: found } = useApi<Hit[]>('/search', { query: { q: asked } })
  const hits = asked.length < 2 ? [] : (found.data ?? [])

  const results = useMemo<Result[]>(() => {
    const q = query.trim().toLowerCase()
    const all: Result[] = [
      ...clients.map((c) => ({ id: c.id, title: c.name, subtitle: c.industry, icon: Building2, to: `/clients/${c.id}`, group: 'Clients' })),
      ...studioProjects.map((p) => ({
        id: p.id,
        title: p.name,
        subtitle: `${STATUS_LABEL[p.status]}${p.client_name === null ? '' : ` · ${p.client_name}`}${p.tasks === 0 ? '' : ` · ${progressOf(p) ?? 0}%`}`,
        icon: Layers,
        to: `/projects/${p.id}`,
        group: 'Projects'
      })),
      ...projects.map((p) => ({ id: p.id, title: p.name, subtitle: `${p.status} · ${p.progress}% complete`, icon: FolderKanban, to: '/board', group: 'The month' })),
      ...tasks.map((t) => {
        const matchedNote = query.trim() && t.notes?.find((n) => n.text.toLowerCase().includes(query.trim().toLowerCase()))
        return {
          id: t.id,
          title: t.title,
          subtitle: matchedNote ? `Note by ${matchedNote.authorName}: "${matchedNote.text}"` : `${t.status} · ${t.priority}`,
          icon: matchedNote ? MessageSquare : ClipboardList,
          to: '/board',
          group: 'Tasks',
        }
      }),
      ...team.map((m) => ({ id: m.id, title: m.name, subtitle: m.title, icon: Users, to: `/people/${m.id}`, group: 'Team' })),
      ...content.map((c) => ({ id: c.id, title: c.title, subtitle: `${c.platform} · ${c.contentType}`, icon: FileText, to: '/content', group: 'Content' })),
    ]
    if (!q) return all.slice(0, 8)

    const here = all.filter((r) => r.title.toLowerCase().includes(q) || r.subtitle.toLowerCase().includes(q))
    // What is already loaded answers instantly; what the server found fills in
    // a moment later. The same thing found twice is shown once.
    const seen = new Set(here.map((r) => `${r.group}:${r.id}`))
    const fromServer = hits
      .map(asResult)
      .filter((r) => !seen.has(`${r.group}:${r.id}`))
    return [...here, ...fromServer].slice(0, 25)
  }, [query, clients, projects, studioProjects, tasks, team, content, hits])

  const grouped = useMemo(() => {
    const map = new Map<string, Result[]>()
    results.forEach((r) => {
      if (!map.has(r.group)) map.set(r.group, [])
      map.get(r.group)!.push(r)
    })
    return Array.from(map.entries())
  }, [results])

  function close() {
    setCommandPaletteOpen(false)
    setQuery('')
  }

  return createPortal(
    <AnimatePresence>
      {commandPaletteOpen && (
        <div className="fixed inset-0 z-[300] flex items-start justify-center px-4 pt-[12vh]">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="absolute inset-0 bg-ink-950/50 backdrop-blur-sm"
            onClick={close}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: -6, transition: { duration: 0.14 } }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 w-full max-w-xl overflow-hidden rounded-2xl border border-shell-border bg-shell shadow-pop"
          >
            <div className="flex items-center gap-3 border-b border-shell-border px-4 py-3.5">
              <Search size={17} className="text-shell-text" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search work, clients, people…"
                aria-label="Command palette search"
                className="flex-1 bg-transparent text-base text-shell-text-active outline-none placeholder:text-shell-text sm:text-sm"
              />
              <kbd className="rounded-md border border-shell-border bg-shell-border/40 px-1.5 py-0.5 text-[10px] font-medium text-shell-text">
                ESC
              </kbd>
            </div>
            <div className="max-h-96 overflow-y-auto scrollbar-thin p-2">
              {grouped.length === 0 && <p className="px-3 py-8 text-center text-sm text-shell-text">No results found.</p>}
              {grouped.map(([group, items]) => (
                <div key={group} className="mb-1">
                  <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-shell-text">{group}</p>
                  {items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        navigate(item.to)
                        close()
                      }}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-shell-border/40',
                      )}
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-shell-border text-shell-text-active">
                        <item.icon size={15} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-shell-text-active">{item.title}</p>
                        <p className="truncate text-xs text-shell-text">{item.subtitle}</p>
                      </div>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
